import React, { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { useI18n } from "../lib/i18n";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import {
  Handshake, Plus, X, ChevronDown, ChevronRight, ArrowRightLeft,
  UserPlus, Trash2, ShieldCheck, Building2, Power, SlidersHorizontal, FileDown, FileSpreadsheet, Send,
} from "lucide-react";
import { exportCsv } from "../lib/exportCsv";

const MANAGER_ROLES = ["manager", "hr", "admin", "ceo"];

function Badge({ children, tone = "slate", testid }) {
  const tones = {
    slate: "bg-[#e2e8f0] text-[#475569]",
    green: "bg-[#dcfce7] text-[#166534]",
    amber: "bg-[#fcf3e2] text-[#a38d5b]",
    rose: "bg-[#fee2e2] text-[#9f1239]",
  };
  return (
    <span data-testid={testid} className={`inline-flex px-2 py-0.5 rounded-full text-[10.5px] font-semibold uppercase tracking-wide ${tones[tone] || tones.slate}`}>
      {children}
    </span>
  );
}

function Modal({ title, onClose, children, testid }) {
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => onClose(false)}>
      <div className="bg-white rounded-[20px] max-w-[560px] w-full p-6 shadow-2xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid={testid}>
        <div className="flex items-start justify-between mb-4">
          <h3 className="sw-heading text-[19px]">{title}</h3>
          <button onClick={() => onClose(false)} className="text-[#94a3b8]" data-testid={`${testid}-close`}><X size={20} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

/* ─────────── Create contract ─────────── */
function CreateContractModal({ partners, myTenantId, onClose }) {
  const { t } = useI18n();
  const [myRole, setMyRole] = useState("client");
  const [partner, setPartner] = useState("");
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [description, setDescription] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [saving, setSaving] = useState(false);
  const others = partners.filter((p) => p.id !== myTenantId);

  const save = async () => {
    if (!partner || !name.trim()) { toast.error(t("sc.fill_required")); return; }
    setSaving(true);
    try {
      await api.post("/service-contracts", {
        provider_tenant_id: myRole === "provider" ? myTenantId : partner,
        client_tenant_id: myRole === "client" ? myTenantId : partner,
        name: name.trim(),
        code: code || null,
        description: description || null,
        start_date: startDate || null,
        end_date: endDate || null,
      });
      toast.success(t("sc.created"));
      onClose(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("sc.error"));
    } finally { setSaving(false); }
  };

  return (
    <Modal title={t("sc.new_contract")} onClose={onClose} testid="sc-create-modal">
      <div className="grid grid-cols-2 gap-3 mb-3">
        <div>
          <label className="sw-label">{t("sc.my_role")}</label>
          <select value={myRole} onChange={(e) => setMyRole(e.target.value)} className="sw-input" data-testid="sc-create-myrole">
            <option value="client">{t("sc.client")}</option>
            <option value="provider">{t("sc.provider")}</option>
          </select>
        </div>
        <div>
          <label className="sw-label">{t("sc.partner_company")}</label>
          <select value={partner} onChange={(e) => setPartner(e.target.value)} className="sw-input" data-testid="sc-create-partner">
            <option value="">—</option>
            {others.map((p) => <option key={p.id} value={p.id}>{p.short_name || p.name}</option>)}
          </select>
        </div>
      </div>
      <label className="sw-label">{t("sc.contract_name")}</label>
      <input value={name} onChange={(e) => setName(e.target.value)} className="sw-input" placeholder="Ex: Prestação de Serviços — Projetos 2026" data-testid="sc-create-name" />
      <div className="grid grid-cols-3 gap-3 mt-3">
        <div>
          <label className="sw-label">{t("sc.code")}</label>
          <input value={code} onChange={(e) => setCode(e.target.value)} className="sw-input" data-testid="sc-create-code" />
        </div>
        <div>
          <label className="sw-label">{t("sc.start")}</label>
          <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="sw-input" data-testid="sc-create-start" />
        </div>
        <div>
          <label className="sw-label">{t("sc.end")}</label>
          <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="sw-input" data-testid="sc-create-end" />
        </div>
      </div>
      <label className="sw-label mt-3">{t("sc.description")}</label>
      <textarea rows={2} value={description} onChange={(e) => setDescription(e.target.value)} className="sw-input" data-testid="sc-create-desc" />
      <div className="flex gap-2 mt-4">
        <button onClick={save} disabled={saving} className="sw-btn-primary" data-testid="sc-create-save">
          {saving ? t("common.saving") : t("sc.create_btn")}
        </button>
        <button onClick={() => onClose(false)} className="sw-btn-secondary">{t("common.cancel")}</button>
      </div>
    </Modal>
  );
}

/* ─────────── Allocation cost / rate config ─────────── */
function AllocationCostConfig({ contractId, alloc, depts, accounts, isClient, onSaved }) {
  const { t } = useI18n();
  const [f, setF] = useState({
    cost_item_id: alloc.cost_item_id || "",
    cost_item_label: alloc.cost_item_label || "",
    department_id: alloc.department_id || "",
    fee_cost_item_id: alloc.fee_cost_item_id || "",
    fee_cost_item_label: alloc.fee_cost_item_label || "",
    fee_department_id: alloc.fee_department_id || "",
    billing_rate_type: alloc.billing_rate_type || "",
    billing_rate: alloc.billing_rate ?? "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const renderAcct = (idKey, labelKey, testid) => (accounts.length > 0 ? (
    <select
      value={f[idKey]}
      onChange={(e) => {
        const acc = accounts.find((a) => String(a.Id) === e.target.value);
        setF((p) => ({ ...p, [idKey]: e.target.value, [labelKey]: acc ? (acc.FullyQualifiedName || acc.Name) : "" }));
      }}
      className="sw-input !py-1.5 !text-[12.5px]"
      data-testid={testid}
    >
      <option value="">—</option>
      {accounts.map((a) => <option key={a.Id} value={String(a.Id)}>{a.FullyQualifiedName || a.Name}</option>)}
    </select>
  ) : (
    <input
      value={f[labelKey]}
      onChange={(e) => setF((p) => ({ ...p, [labelKey]: e.target.value, [idKey]: "" }))}
      placeholder={t("sc.cost_item_free")}
      className="sw-input !py-1.5 !text-[12.5px]"
      data-testid={testid}
    />
  ));

  const renderDept = (key, testid) => (
    <select value={f[key]} onChange={(e) => set(key, e.target.value)} className="sw-input !py-1.5 !text-[12.5px]" data-testid={testid}>
      <option value="">—</option>
      {depts.map((d) => <option key={d.id} value={d.id}>{d.cost_code ? `${d.cost_code} · ${d.name}` : d.name}</option>)}
    </select>
  );

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        billing_rate_type: f.billing_rate_type || "",
        billing_rate: f.billing_rate === "" || f.billing_rate === null ? null : parseFloat(f.billing_rate),
      };
      if (isClient) {
        payload.cost_item_id = f.cost_item_id || "";
        payload.cost_item_label = f.cost_item_label || "";
        payload.department_id = f.department_id || "";
        payload.fee_cost_item_id = f.fee_cost_item_id || "";
        payload.fee_cost_item_label = f.fee_cost_item_label || "";
        payload.fee_department_id = f.fee_department_id || "";
      }
      await api.patch(`/service-contracts/${contractId}/allocations/${alloc.id}`, payload);
      toast.success(t("sc.saved"));
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("sc.error"));
    } finally { setSaving(false); }
  };

  return (
    <div className="p-3" data-testid={`sc-cost-config-${alloc.id}`}>
      <div className="grid sm:grid-cols-2 gap-3">
        {isClient && (
          <>
            <div>
              <label className="sw-label">{t("sc.cost_item")}</label>
              {renderAcct("cost_item_id", "cost_item_label", `sc-cfg-cost-item-${alloc.id}`)}
            </div>
            <div>
              <label className="sw-label">{t("sc.cost_center")}</label>
              {renderDept("department_id", `sc-cfg-cost-center-${alloc.id}`)}
            </div>
          </>
        )}
        <div>
          <label className="sw-label">{t("sc.fee_rate_type")}</label>
          <div className="flex gap-2">
            <select value={f.billing_rate_type} onChange={(e) => set("billing_rate_type", e.target.value)} className="sw-input !py-1.5 !text-[12.5px]" data-testid={`sc-cfg-rate-type-${alloc.id}`}>
              <option value="">{t("sc.rate_none")}</option>
              <option value="hourly">{t("sc.rate_hourly")}</option>
              <option value="monthly">{t("sc.rate_monthly")}</option>
            </select>
            <input
              type="number" min="0" step="0.01"
              value={f.billing_rate}
              onChange={(e) => set("billing_rate", e.target.value)}
              placeholder={t("sc.rate_value")}
              disabled={!f.billing_rate_type}
              className="sw-input !py-1.5 !text-[12.5px] w-32"
              data-testid={`sc-cfg-rate-value-${alloc.id}`}
            />
          </div>
        </div>
        {isClient && (
          <>
            <div>
              <label className="sw-label">{t("sc.fee_cost_item")}</label>
              {renderAcct("fee_cost_item_id", "fee_cost_item_label", `sc-cfg-fee-cost-item-${alloc.id}`)}
            </div>
            <div>
              <label className="sw-label">{t("sc.fee_cost_center")}</label>
              {renderDept("fee_department_id", `sc-cfg-fee-cost-center-${alloc.id}`)}
              <div className="text-[10.5px] text-[#94a3b8] mt-1">{t("sc.fee_same_hint")}</div>
            </div>
          </>
        )}
      </div>
      <button onClick={save} disabled={saving} className="sw-btn-primary mt-3" data-testid={`sc-cfg-save-${alloc.id}`}>
        {saving ? t("common.saving") : t("sc.saved_btn")}
      </button>
    </div>
  );
}

/* ─────────── Monthly billing summary ─────────── */
function BillingSection({ contractId, refreshKey, side }) {
  const { t } = useI18n();
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [data, setData] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const isProvider = side === "provider" || side === "both";

  const load = React.useCallback(() => {
    api.get(`/service-contracts/${contractId}/billing?month=${month}`)
      .then((r) => setData(r.data)).catch(() => setData(null));
  }, [contractId, month, refreshKey]); // eslint-disable-line
  useEffect(() => { load(); }, [load]);

  const saveHours = async (allocId, val) => {
    try {
      await api.patch(`/service-contracts/${contractId}/billing/hours`, {
        allocation_id: allocId, month, hours: parseFloat(val || "0"),
      });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || t("sc.error")); }
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data: res } = await api.post(`/service-contracts/${contractId}/billing/submit`, { month });
      toast.success(t("sc.appr_submitted_n", { n: res.submitted }));
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || t("sc.error")); }
    finally { setSubmitting(false); }
  };

  const apprBadge = (r) => {
    const ap = r.approval;
    const base = "inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide whitespace-nowrap";
    if (!ap) return <span className={`${base} bg-[#e2e8f0] text-[#64748b]`} data-testid={`sc-appr-badge-${r.allocation_id}`}>{t("sc.appr_not_submitted")}</span>;
    const map = {
      pending: ["bg-[#fcf3e2] text-[#a38d5b]", t("sc.appr_pending")],
      approved: ["bg-[#dcfce7] text-[#166534]", t("sc.appr_approved")],
      rejected: ["bg-[#fee2e2] text-[#9f1239]", t("sc.appr_rejected")],
    };
    const [cls, label] = map[ap.status] || map.pending;
    return (
      <div className="flex flex-col gap-1 items-start">
        <span className={`${base} ${cls}`} data-testid={`sc-appr-badge-${r.allocation_id}`} title={ap.decision_comment || ""}>{label}</span>
        {ap.stale && <span className={`${base} bg-[#fff7ed] text-[#c2410c]`}>{t("sc.appr_stale")}</span>}
        {ap.status === "rejected" && ap.decision_comment && (
          <span className="text-[10px] text-[#9f1239] max-w-[150px] leading-tight">{ap.decision_comment}</span>
        )}
      </div>
    );
  };

  const csv = () => {
    if (!data || !data.rows.length) return;
    exportCsv(`faturamento-${data.contract_code || contractId.slice(0, 8)}-${month}.csv`,
      data.rows.map((r) => ({
        pessoa: r.user_name, funcao: r.job_title || "",
        tipo_custo: r.fee_cost_item_label || "", centro_custo: r.fee_department_label || "",
        taxa: r.billing_rate_type || "", valor_taxa: r.billing_rate || 0, horas: r.hours,
        recurso_usd: r.fee_usd, reembolsos_qtd: r.reimb_count, reembolsos_usd: r.reimb_usd,
        viagens_qtd: r.travel_count, viagens_usd: r.travel_usd, total_usd: r.total_usd,
        aprovacao: r.approval ? r.approval.status : "nao_enviado",
        aprovacao_comentario: r.approval?.decision_comment || "",
      })));
  };

  const pdf = async () => {
    try {
      const res = await api.get(`/service-contracts/${contractId}/billing/pdf?month=${month}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `faturamento-${month}.pdf`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch { toast.error(t("sc.error")); }
  };

  const money = (v) => `$${(v || 0).toFixed(2)}`;
  const rateFmt = (r) => (r.billing_rate
    ? (r.billing_rate_type === "hourly" ? `${money(r.billing_rate)}/h` : `${money(r.billing_rate)}/${t("sc.rate_month_short")}`)
    : "—");

  return (
    <div className="mt-5 border-t border-[#e2e8f0] pt-4" data-testid={`sc-billing-${contractId}`}>
      <div className="flex items-end justify-between flex-wrap gap-3 mb-3">
        <div>
          <div className="flex items-center gap-2">
            <FileSpreadsheet size={15} className="text-[#475569]" />
            <h3 className="sw-heading text-[15px]">{t("sc.billing_title")}</h3>
          </div>
          <p className="text-[11.5px] text-[#64748b]">{t("sc.billing_subtitle")}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="sw-input !py-1.5 !text-[12.5px] w-40" data-testid="sc-billing-month" />
          {isProvider && (
            <button onClick={submit} disabled={submitting} className="sw-btn-primary !py-1.5 !text-[12px]" data-testid="sc-billing-submit">
              <Send size={13} /> {t("sc.appr_submit")}
            </button>
          )}
          <button onClick={csv} className="sw-btn-secondary !py-1.5 !text-[12px]" data-testid="sc-billing-csv"><FileDown size={13} /> CSV</button>
          <button onClick={pdf} className="sw-btn-secondary !py-1.5 !text-[12px]" data-testid="sc-billing-pdf"><FileDown size={13} /> PDF</button>
        </div>
      </div>
      {data && (
        <div className="flex flex-wrap gap-2 mb-3" data-testid="sc-appr-totals">
          {[
            ["requested", t("sc.appr_requested"), data.approval_totals?.requested_usd, "bg-[#f1f5f9] text-[#334155]"],
            ["approved", t("sc.appr_approved"), data.approval_totals?.approved_usd, "bg-[#dcfce7] text-[#166534]"],
            ["pending", t("sc.appr_in_review"), data.approval_totals?.pending_usd, "bg-[#fcf3e2] text-[#a38d5b]"],
            ["rejected", t("sc.appr_rejected"), data.approval_totals?.rejected_usd, "bg-[#fee2e2] text-[#9f1239]"],
          ].map(([key, label, v, cls]) => (
            <div key={key} className={`px-3 py-1.5 rounded-[10px] text-[11.5px] font-medium ${cls}`} data-testid={`sc-appr-total-${key}`}>
              {label}: <span className="font-bold">{money(v || 0)}</span>
            </div>
          ))}
        </div>
      )}
      {!data || data.rows.length === 0 ? (
        <div className="text-[12.5px] text-[#94a3b8] py-2">{t("sc.billing_empty")}</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-[12.5px]">
            <thead>
              <tr className="text-left border-b border-[#e2e8f0]">
                {[t("sc.billing_person"), t("sc.cost_item_short"), t("sc.cost_center_short"), t("sc.billing_rate"), t("sc.billing_hours"), t("sc.billing_fee"), t("sc.billing_reimb"), t("sc.billing_travel"), t("sc.billing_total"), t("sc.appr_col")].map((h) => (
                  <th key={h} className="py-2 pr-3 text-[10px] uppercase tracking-[0.12em] text-[#94a3b8] font-semibold whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r) => (
                <tr key={r.allocation_id} className="border-b border-[#f1f5f9]" data-testid={`sc-billing-row-${r.allocation_id}`}>
                  <td className="py-2 pr-3">
                    <div className="font-medium text-[#1e293b]">{r.user_name}</div>
                    <div className="text-[10.5px] text-[#94a3b8]">{r.job_title || "—"}{!r.active && ` · ${t("sc.status_inactive")}`}</div>
                  </td>
                  <td className="py-2 pr-3 text-[#475569]">{r.fee_cost_item_label || "—"}</td>
                  <td className="py-2 pr-3 text-[#475569]">{r.fee_department_label || "—"}</td>
                  <td className="py-2 pr-3 whitespace-nowrap">{rateFmt(r)}</td>
                  <td className="py-2 pr-3">
                    {r.billing_rate_type === "hourly" ? (
                      <input
                        type="number" min="0" step="0.5"
                        key={`${r.allocation_id}-${month}-${r.hours}`}
                        defaultValue={r.hours}
                        onBlur={(e) => parseFloat(e.target.value || "0") !== r.hours && saveHours(r.allocation_id, e.target.value)}
                        className="sw-input !py-1 !text-[12px] w-20"
                        data-testid={`sc-billing-hours-${r.allocation_id}`}
                      />
                    ) : "—"}
                  </td>
                  <td className="py-2 pr-3 whitespace-nowrap">{money(r.fee_usd)}</td>
                  <td className="py-2 pr-3 whitespace-nowrap">{money(r.reimb_usd)} <span className="text-[#94a3b8]">({r.reimb_count})</span></td>
                  <td className="py-2 pr-3 whitespace-nowrap">{money(r.travel_usd)} <span className="text-[#94a3b8]">({r.travel_count})</span></td>
                  <td className="py-2 pr-3 font-semibold text-[#1e293b] whitespace-nowrap" data-testid={`sc-billing-total-${r.allocation_id}`}>{money(r.total_usd)}</td>
                  <td className="py-2 pr-3">{apprBadge(r)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-[#1e293b]">
                <td colSpan={5} className="py-2 pr-3 font-semibold text-[#1e293b]">{t("sc.billing_grand")}</td>
                <td className="py-2 pr-3 font-semibold whitespace-nowrap">{money(data.totals.fees_usd)}</td>
                <td className="py-2 pr-3 font-semibold whitespace-nowrap">{money(data.totals.reimb_usd)}</td>
                <td className="py-2 pr-3 font-semibold whitespace-nowrap">{money(data.totals.travel_usd)}</td>
                <td className="py-2 pr-3 font-bold text-[#1e293b] whitespace-nowrap" data-testid="sc-billing-grand">{money(data.totals.grand_usd)}</td>
                <td></td>
              </tr>
            </tfoot>
          </table>
          <div className="text-[10.5px] text-[#94a3b8] mt-2">{t("sc.billing_criteria")}</div>
        </div>
      )}
    </div>
  );
}

/* ─────────── Contract detail (allocations) ─────────── */
function ContractDetail({ contractId, myUsers, onChanged }) {
  const { t } = useI18n();
  const [c, setC] = useState(null);
  const [addUserId, setAddUserId] = useState("");
  const [busy, setBusy] = useState(false);
  const [cfgId, setCfgId] = useState(null);
  const [depts, setDepts] = useState([]);
  const [accounts, setAccounts] = useState([]);

  const load = () => api.get(`/service-contracts/${contractId}`).then((r) => setC(r.data)).catch(() => {});
  useEffect(() => { load(); }, [contractId]); // eslint-disable-line

  const clientSide = c && (c.side === "client" || c.side === "both");
  useEffect(() => {
    if (!clientSide) return;
    api.get("/departments").then((r) => setDepts(r.data || [])).catch(() => {});
    api.get("/qbo/catalog/categories").then((r) => {
      const items = (r.data?.items || []).filter((a) => a.Classification === "Expense" && a.Active !== false);
      const sel = (r.data?.selected_ids || []).map(String);
      setAccounts(sel.length ? items.filter((a) => sel.includes(String(a.Id))) : items);
    }).catch(() => setAccounts([]));
  }, [clientSide]); // eslint-disable-line

  const clientManagers = useMemo(() => myUsers.filter((u) => MANAGER_ROLES.includes(u.role)), [myUsers]);
  if (!c) return <div className="text-[#94a3b8] text-[13px] py-3">{t("common.loading")}</div>;

  const isProvider = c.side === "provider" || c.side === "both";
  const isClient = c.side === "client" || c.side === "both";
  const allocs = c.allocations || [];
  const allocatedIds = new Set(allocs.filter((a) => a.active !== false).map((a) => a.user_id));
  const addable = myUsers.filter((u) => !allocatedIds.has(u.id) && u.role !== "superadmin");

  const run = async (fn, okMsg) => {
    setBusy(true);
    try { await fn(); toast.success(okMsg || t("sc.saved")); await load(); onChanged?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || t("sc.error")); }
    finally { setBusy(false); }
  };

  const addPerson = () => {
    if (!addUserId) return;
    run(() => api.post(`/service-contracts/${c.id}/allocations`, { user_ids: [addUserId] }));
    setAddUserId("");
  };

  return (
    <div className="border-t border-[#e2e8f0] pt-4 mt-3" data-testid={`sc-detail-${c.id}`}>
      <div className="text-[11px] text-[#64748b] mb-3">
        {isProvider && <div>{t("sc.provider_hint")}</div>}
        {isClient && <div>{t("sc.client_hint")}</div>}
      </div>

      {isProvider && (
        <div className="flex gap-2 items-end mb-4 flex-wrap">
          <div className="flex-1 min-w-[220px]">
            <label className="sw-label">{t("sc.add_people")}</label>
            <select value={addUserId} onChange={(e) => setAddUserId(e.target.value)} className="sw-input" data-testid="sc-add-person-select">
              <option value="">{t("sc.select_employee")}</option>
              {addable.map((u) => <option key={u.id} value={u.id}>{u.name} — {u.job_title || u.role}</option>)}
            </select>
          </div>
          <button onClick={addPerson} disabled={!addUserId || busy} className="sw-btn-primary" data-testid="sc-add-person-btn">
            <UserPlus size={14} /> {t("sc.allocate")}
          </button>
        </div>
      )}

      {allocs.length === 0 && <div className="text-[13px] text-[#94a3b8] py-3">{t("sc.no_allocations")}</div>}

      {allocs.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead>
              <tr className="text-left border-b border-[#e2e8f0]">
                <th className="py-2 pr-3 text-[10.5px] uppercase tracking-[0.12em] text-[#94a3b8] font-semibold">{t("sc.col_person")}</th>
                <th className="py-2 pr-3 text-[10.5px] uppercase tracking-[0.12em] text-[#94a3b8] font-semibold">{t("sc.designated_manager")}</th>
                <th className="py-2 pr-3 text-[10.5px] uppercase tracking-[0.12em] text-[#94a3b8] font-semibold">{t("common.status")}</th>
                <th className="py-2"></th>
              </tr>
            </thead>
            <tbody>
              {allocs.map((a) => (
                <React.Fragment key={a.id}>
                <tr className="border-b border-[#f1f5f9]" data-testid={`sc-alloc-${a.id}`}>
                  <td className="py-2.5 pr-3">
                    <div className="font-medium text-[#1e293b]">{a.user?.name || a.user_id}</div>
                    <div className="text-[11px] text-[#94a3b8]">{a.user?.job_title || "—"}</div>
                  </td>
                  <td className="py-2.5 pr-3">
                    {isClient ? (
                      <select
                        value={a.client_manager_id || ""}
                        disabled={busy}
                        onChange={(e) => run(() => api.patch(`/service-contracts/${c.id}/allocations/${a.id}`, { client_manager_id: e.target.value || null }))}
                        className="sw-input !py-1.5 !text-[12.5px]"
                        data-testid={`sc-alloc-manager-${a.id}`}
                      >
                        <option value="">{t("sc.no_manager")}</option>
                        {clientManagers.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                      </select>
                    ) : (
                      <span className="text-[#475569]">{a.client_manager?.name || <span className="text-[#cbd5e1]">{t("sc.no_manager")}</span>}</span>
                    )}
                  </td>
                  <td className="py-2.5 pr-3">
                    <Badge tone={a.active !== false ? "green" : "rose"}>{a.active !== false ? t("sc.status_active") : t("sc.status_inactive")}</Badge>
                  </td>
                  <td className="py-2.5 text-right whitespace-nowrap">
                    <button
                      onClick={() => setCfgId(cfgId === a.id ? null : a.id)}
                      className={`p-1 ${cfgId === a.id ? "text-[#1e293b]" : "text-[#475569] hover:text-[#1e293b]"}`}
                      title={t("sc.costs")}
                      aria-label={t("sc.costs")}
                      data-testid={`sc-alloc-costs-${a.id}`}
                    >
                      <SlidersHorizontal size={14} />
                    </button>
                    {isProvider && (
                      <>
                        <button
                          onClick={() => run(() => api.patch(`/service-contracts/${c.id}/allocations/${a.id}`, { active: a.active === false }))}
                          className="text-[#475569] hover:text-[#1e293b] p-1"
                          title={a.active !== false ? t("sc.deactivate") : t("sc.activate")}
                          aria-label={a.active !== false ? t("sc.deactivate") : t("sc.activate")}
                          data-testid={`sc-alloc-toggle-${a.id}`}
                        >
                          <Power size={14} />
                        </button>
                        <button
                          onClick={() => run(() => api.delete(`/service-contracts/${c.id}/allocations/${a.id}`), t("sc.removed"))}
                          className="text-[#94a3b8] hover:text-[#881a27] p-1 ml-1"
                          title={t("sc.remove")}
                          aria-label={t("sc.remove")}
                          data-testid={`sc-alloc-remove-${a.id}`}
                        >
                          <Trash2 size={14} />
                        </button>
                      </>
                    )}
                  </td>
                </tr>
                {cfgId === a.id && (
                  <tr>
                    <td colSpan={4} className="bg-[#f8fafc] rounded-[10px]">
                      <AllocationCostConfig
                        contractId={c.id}
                        alloc={a}
                        depts={depts}
                        accounts={accounts}
                        isClient={isClient}
                        onSaved={() => { setCfgId(null); load(); onChanged?.(); }}
                      />
                    </td>
                  </tr>
                )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <BillingSection contractId={c.id} refreshKey={c.updated_at || ""} side={c.side} />
    </div>
  );
}

/* ─────────── Transfer employee between companies ─────────── */
function TransferModal({ myUsers, partners, contracts, myTenantId, onClose }) {
  const { t } = useI18n();
  const [userId, setUserId] = useState("");
  const [target, setTarget] = useState("");
  const [contractId, setContractId] = useState("");
  const [managerId, setManagerId] = useState("");
  const [saving, setSaving] = useState(false);

  const targets = partners.filter((p) => p.id !== myTenantId);
  const eligibleContracts = contracts.filter((c) => c.provider_tenant_id === target && c.client_tenant_id === myTenantId && c.status === "active");
  const managers = myUsers.filter((u) => MANAGER_ROLES.includes(u.role));

  const save = async () => {
    if (!userId || !target) { toast.error(t("sc.fill_required")); return; }
    setSaving(true);
    try {
      const { data } = await api.post(`/users/${userId}/transfer-tenant`, {
        target_tenant_id: target,
        service_contract_id: contractId || null,
        client_manager_id: managerId || null,
      });
      toast.success(`${t("sc.transfer_done")} — ${Object.values(data.moved_history || {}).reduce((a, b) => a + b, 0)} docs`);
      onClose(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("sc.error"));
    } finally { setSaving(false); }
  };

  return (
    <Modal title={t("sc.transfer_title")} onClose={onClose} testid="sc-transfer-modal">
      <p className="text-[12.5px] text-[#64748b] mb-4">{t("sc.transfer_desc")}</p>
      <label className="sw-label">{t("sc.transfer_user")}</label>
      <select value={userId} onChange={(e) => setUserId(e.target.value)} className="sw-input" data-testid="sc-transfer-user">
        <option value="">—</option>
        {myUsers.filter((u) => u.role !== "superadmin").map((u) => <option key={u.id} value={u.id}>{u.name} — {u.email}</option>)}
      </select>
      <label className="sw-label mt-3">{t("sc.transfer_target")}</label>
      <select value={target} onChange={(e) => { setTarget(e.target.value); setContractId(""); }} className="sw-input" data-testid="sc-transfer-target">
        <option value="">—</option>
        {targets.map((p) => <option key={p.id} value={p.id}>{p.short_name || p.name}</option>)}
      </select>
      {target && (
        <>
          <label className="sw-label mt-3">{t("sc.transfer_contract")}</label>
          <select value={contractId} onChange={(e) => setContractId(e.target.value)} className="sw-input" data-testid="sc-transfer-contract">
            <option value="">—</option>
            {eligibleContracts.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          {contractId && (
            <>
              <label className="sw-label mt-3">{t("sc.transfer_manager")}</label>
              <select value={managerId} onChange={(e) => setManagerId(e.target.value)} className="sw-input" data-testid="sc-transfer-manager">
                <option value="">—</option>
                {managers.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
              </select>
            </>
          )}
        </>
      )}
      <div className="mt-4 p-3 rounded-[12px] bg-[#fcf3e2] text-[#7c6a3f] text-[12px]" data-testid="sc-transfer-warning">
        {t("sc.transfer_warning")}
      </div>
      <div className="flex gap-2 mt-4">
        <button onClick={save} disabled={saving} className="sw-btn-primary" data-testid="sc-transfer-confirm">
          <ArrowRightLeft size={14} /> {saving ? t("common.saving") : t("sc.transfer_confirm")}
        </button>
        <button onClick={() => onClose(false)} className="sw-btn-secondary">{t("common.cancel")}</button>
      </div>
    </Modal>
  );
}

/* ─────────── Multi-company access (allowed_tenants) ─────────── */
function AccessSection() {
  const { t } = useI18n();
  const [list, setList] = useState([]);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/tenant-access").then((r) => setList(r.data || [])).catch(() => setList([]));
  useEffect(() => { load(); }, []);

  const grant = async () => {
    if (!email.trim()) return;
    setBusy(true);
    try {
      await api.post("/tenant-access/grant", { email: email.trim() });
      toast.success(t("sc.saved"));
      setEmail("");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || t("sc.error")); }
    finally { setBusy(false); }
  };

  const revoke = async (em) => {
    try { await api.post("/tenant-access/revoke", { email: em }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || t("sc.error")); }
  };

  return (
    <div className="sw-card" data-testid="sc-access-section">
      <div className="flex items-center gap-2 mb-1">
        <ShieldCheck size={17} className="text-[#475569]" />
        <h2 className="sw-heading text-[17px]">{t("sc.access_title")}</h2>
      </div>
      <p className="text-[12.5px] text-[#64748b] mb-4">{t("sc.access_desc")}</p>
      <div className="flex gap-2 flex-wrap">
        <input
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder={t("sc.access_email")}
          className="sw-input flex-1 min-w-[240px]"
          data-testid="sc-access-email"
        />
        <button onClick={grant} disabled={busy || !email.trim()} className="sw-btn-primary" data-testid="sc-access-grant">
          <Plus size={14} /> {t("sc.access_grant")}
        </button>
      </div>
      <div className="mt-4">
        {list.length === 0 && <div className="text-[12.5px] text-[#94a3b8]">{t("sc.access_none")}</div>}
        {list.map((u) => (
          <div key={u.id} className="flex items-center justify-between py-2 border-b border-[#f1f5f9] last:border-0" data-testid={`sc-access-row-${u.id}`}>
            <div>
              <div className="text-[13px] font-medium text-[#1e293b]">{u.name} <span className="text-[#94a3b8] font-normal">· {u.email}</span></div>
              <div className="text-[11px] text-[#94a3b8]">{u.home_tenant_name} · {u.role}</div>
            </div>
            <button onClick={() => revoke(u.email)} className="text-[12px] text-[#881a27] hover:underline" data-testid={`sc-access-revoke-${u.id}`}>
              {t("sc.access_revoke")}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─────────── Page ─────────── */
export default function ServiceContracts() {
  const { t } = useI18n();
  const { user } = useAuth();
  const [contracts, setContracts] = useState([]);
  const [partners, setPartners] = useState([]);
  const [myUsers, setMyUsers] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [showTransfer, setShowTransfer] = useState(false);
  const [loading, setLoading] = useState(true);

  const myTenantId = user?.tenant_id;
  const isAdmin = user?.role === "admin" || user?.role === "superadmin";

  const load = async () => {
    try {
      const [c, p, u] = await Promise.all([
        api.get("/service-contracts"),
        api.get("/service-contracts/partners"),
        api.get("/users"),
      ]);
      setContracts(c.data || []);
      setPartners(p.data || []);
      setMyUsers((u.data || []).filter((x) => x.active !== false));
    } catch { /* ignore */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const toggleStatus = async (c) => {
    try {
      await api.patch(`/service-contracts/${c.id}`, { status: c.status === "active" ? "ended" : "active" });
      toast.success(t("sc.saved"));
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || t("sc.error")); }
  };

  if (loading) return <div className="text-[#94a3b8]">{t("common.loading")}</div>;

  return (
    <div className="flex flex-col gap-6" data-testid="service-contracts-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-[14px] bg-[#e2e8f0] text-[#475569] flex items-center justify-center"><Handshake size={22} /></div>
          <div>
            <h1 className="sw-heading text-[28px]">{t("sc.title")}</h1>
            <p className="text-[13.5px] text-[#747474]">{t("sc.subtitle")}</p>
          </div>
        </div>
        <div className="flex gap-2">
          {isAdmin && (
            <button onClick={() => setShowTransfer(true)} className="sw-btn-secondary" data-testid="sc-transfer-open">
              <ArrowRightLeft size={14} /> {t("sc.transfer_btn")}
            </button>
          )}
          <button onClick={() => setShowCreate(true)} className="sw-btn-primary" data-testid="sc-create-open">
            <Plus size={14} /> {t("sc.new_contract")}
          </button>
        </div>
      </div>

      {contracts.length === 0 && (
        <div className="sw-card text-center py-12 text-[#747474]" data-testid="sc-empty">{t("sc.no_contracts")}</div>
      )}

      {contracts.map((c) => (
        <div key={c.id} className="sw-card" data-testid={`sc-card-${c.id}`}>
          <div className="w-full flex items-center gap-3 text-left cursor-pointer" onClick={() => setOpenId(openId === c.id ? null : c.id)} data-testid={`sc-card-toggle-${c.id}`}>
            {openId === c.id ? <ChevronDown size={16} className="text-[#94a3b8] shrink-0" /> : <ChevronRight size={16} className="text-[#94a3b8] shrink-0" />}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-medium text-[15px] text-[#1e293b]">{c.name}</span>
                <Badge tone={c.status === "active" ? "green" : "rose"}>{c.status === "active" ? t("sc.status_active") : t("sc.status_ended")}</Badge>
                {c.side === "provider" && <Badge tone="amber">{t("sc.provider")}</Badge>}
                {c.side === "client" && <Badge tone="slate">{t("sc.client")}</Badge>}
              </div>
              <div className="text-[12.5px] text-[#64748b] mt-0.5 flex items-center gap-1.5 flex-wrap">
                <Building2 size={12} /> {c.provider_tenant_name}
                <span className="text-[#cbd5e1]">→</span>
                {c.client_tenant_name}
                <span className="text-[#cbd5e1]">·</span>
                {t("sc.allocated_n", { n: c.active_allocations ?? 0 })}
                {c.code && <><span className="text-[#cbd5e1]">·</span> {c.code}</>}
              </div>
            </div>
            <button
              onClick={(e) => { e.stopPropagation(); toggleStatus(c); }}
              className="text-[11.5px] text-[#475569] hover:text-[#1e293b] border border-[#e2e8f0] rounded-full px-3 py-1 shrink-0"
              data-testid={`sc-status-toggle-${c.id}`}
            >
              {c.status === "active" ? t("sc.end_contract") : t("sc.reactivate_contract")}
            </button>
          </div>
          {openId === c.id && <ContractDetail contractId={c.id} myUsers={myUsers} onChanged={load} />}
        </div>
      ))}

      {isAdmin && <AccessSection />}

      {showCreate && (
        <CreateContractModal partners={partners} myTenantId={myTenantId} onClose={(saved) => { setShowCreate(false); if (saved) load(); }} />
      )}
      {showTransfer && (
        <TransferModal myUsers={myUsers} partners={partners} contracts={contracts} myTenantId={myTenantId} onClose={(done) => { setShowTransfer(false); if (done) load(); }} />
      )}
    </div>
  );
}
